import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Space, Badge, Drawer } from 'antd';
import {
  DashboardOutlined,
  ShoppingOutlined,
  MessageOutlined,
  UserOutlined,
  SettingOutlined,
  BellOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  StarOutlined,
  CalendarOutlined,
  MenuOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useAuthStore } from './stores/authStore';
import { useQuery } from 'react-query';
import api from './services/api';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Orders from './pages/Orders';
import Chat from './pages/Chat';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Login from './pages/Login';
import AssessmentTasks from './pages/AssessmentTasks';
import LocationTracking from './pages/LocationTracking';
import PersistentLocationTracker from './components/PersistentLocationTracker';
import Earnings from './pages/Earnings';
import Reviews from './pages/Reviews';
import Bookings from './pages/Bookings';
import Notifications from './pages/Notifications';
import Questions from './pages/Questions';
import './App.css';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Header, Sider, Content } = Layout;

const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(
  /\/api$/,
  '',
);
const getFullUrl = (url) => (url ? `${BACKEND_URL}${url}` : null);

function App() {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout, checkAuth } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const menuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/assessment-tasks',
      icon: <HomeOutlined />,
      label: 'Assessment Tasks',
    },
    {
      key: '/location-tracking',
      icon: <EnvironmentOutlined />,
      label: 'Location Tracking',
    },
    {
      key: '/earnings',
      icon: <DollarOutlined />,
      label: 'Earnings',
    },
    {
      key: '/reviews',
      icon: <StarOutlined />,
      label: 'Reviews',
    },
    {
      key: '/questions',
      icon: <QuestionCircleOutlined />,
      label: 'Questions',
    },
    {
      key: '/notifications',
      icon: <BellOutlined />,
      label: 'Notifications',
    },
    {
      key: '/bookings',
      icon: <CalendarOutlined />,
      label: 'Bookings',
    },
    {
      key: '/products',
      icon: <ShoppingOutlined />,
      label: 'Products / Services',
    },
    {
      key: '/orders',
      icon: <ShoppingOutlined />,
      label: 'Orders',
    },
    {
      key: '/chat',
      icon: <MessageOutlined />,
      label: 'Chat',
    },
    {
      key: '/profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: logout,
    },
  ];

  // Fetch notifications - only when user is authenticated
  const { data: notificationData } = useQuery(
    'provider-notifications',
    async () => {
      const response = await api.get('/provider/notifications');
      return response.data;
    },
    {
      refetchInterval: 30000,
      enabled: !!user && !!localStorage.getItem('token'),
    },
  );

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const notifications = notificationData?.data || [];
  const unreadCount = notificationData?.unread_count || 0;

  const notificationItems = [
    ...notifications.slice(0, 5).map((n) => ({
      key: n.id,
      label: (
        <div style={{ padding: '4px 0', maxWidth: 280 }} onClick={() => navigate('/notifications')}>
          <div style={{ fontWeight: n.is_read ? 'normal' : 'bold' }}>{n.title}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{n.message}</div>
          <div style={{ fontSize: 10, color: '#aaa', marginTop: 4 }}>
            {dayjs(n.created_at).fromNow()}
          </div>
        </div>
      ),
    })),
    ...(notifications.length > 0
      ? [
          {
            key: 'view-all',
            label: (
              <div
                style={{
                  textAlign: 'center',
                  padding: '8px 0',
                  borderTop: '1px solid #f0f0f0',
                  color: '#1890ff',
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/notifications')}
              >
                View all notifications
              </div>
            ),
          },
        ]
      : []),
  ];

  // Check if user is authenticated
  const isAuthenticated = user && localStorage.getItem('token');

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop sidebar */}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        breakpoint="lg"
        collapsedWidth={0}
        className="site-layout-background desktop-sider"
      >
        <div className="logo">
          <img
            src="/images/currentcare-logo.png"
            alt="Current Care"
            style={{ height: '40px', width: 'auto' }}
            onError={(e) => {
              e.target.src =
                'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22%3E%3Crect width=%22100%22 height=%22100%22 rx=%2220%22 fill=%22%232d5a3d%22/%3E%3Ctext x=%2250%22 y=%2268%22 font-family=%22Arial,sans-serif%22 font-size=%2244%22 font-weight=%22bold%22 fill=%22white%22 text-anchor=%22middle%22%3ECC%3C/text%3E%3C/svg%3E';
            }}
          />
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => {
            navigate(key);
            setMobileDrawerOpen(false);
            if (window.innerWidth < 992) setCollapsed(true);
          }}
        />
      </Sider>

      {/* Mobile drawer */}
      <Drawer
        title={
          <img
            src="/images/currentcare-logo.png"
            alt="Current Care"
            style={{ height: '36px', width: 'auto' }}
            onError={(e) => {
              e.target.src =
                'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22%3E%3Crect width=%22100%22 height=%22100%22 rx=%2220%22 fill=%22%232d5a3d%22/%3E%3Ctext x=%2250%22 y=%2268%22 font-family=%22Arial,sans-serif%22 font-size=%2244%22 font-weight=%22bold%22 fill=%22white%22 text-anchor=%22middle%22%3ECC%3C/text%3E%3C/svg%3E';
            }}
          />
        }
        placement="left"
        onClose={() => setMobileDrawerOpen(false)}
        open={mobileDrawerOpen}
        width={260}
        className="mobile-drawer"
      >
        <div
          style={{
            textAlign: 'center',
            padding: '24px 0',
            borderBottom: '1px solid #f0f0f0',
            marginBottom: 8,
          }}
        >
          <Avatar size={56} src={getFullUrl(user.profile_image)} icon={<UserOutlined />} />
          <div style={{ marginTop: 8, fontWeight: 500 }}>
            {user.full_name || user.name || user.business_name}
          </div>
          <div style={{ fontSize: 12, color: '#888' }}>Provider</div>
        </div>
        <Menu
          mode="vertical"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => {
            navigate(key);
            setMobileDrawerOpen(false);
          }}
          style={{ borderRight: 0 }}
        />
      </Drawer>

      <Layout className="site-layout">
        <Header
          className="site-layout-background"
          style={{ padding: '0 16px', background: '#fff' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Mobile hamburger */}
              <div
                className="mobile-menu-trigger"
                onClick={() => setMobileDrawerOpen(true)}
                style={{ cursor: 'pointer', padding: '0 8px' }}
              >
                <MenuOutlined style={{ fontSize: '18px', color: '#666' }} />
              </div>
              {/* Desktop collapse trigger */}
              <div className="desktop-collapse-trigger">
                {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                  className: 'trigger',
                  onClick: () => setCollapsed(!collapsed),
                })}
              </div>
            </div>
            <Space size="middle">
              <PersistentLocationTracker isAuthenticated={isAuthenticated} />
              <Dropdown
                menu={{ items: notificationItems }}
                placement="bottomRight"
                trigger={['click']}
              >
                <div style={{ cursor: 'pointer' }}>
                  <Badge count={unreadCount} size="small">
                    <BellOutlined style={{ fontSize: '18px', color: '#666' }} />
                  </Badge>
                </div>
              </Dropdown>
              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                <Space style={{ cursor: 'pointer' }}>
                  <Avatar src={getFullUrl(user.profile_image)} icon={<UserOutlined />} />
                  <span className="user-name-desktop">
                    {user.full_name || user.name || user.business_name}
                  </span>
                </Space>
              </Dropdown>
            </Space>
          </div>
        </Header>
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff' }}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/assessment-tasks" element={<AssessmentTasks />} />
            <Route path="/location-tracking" element={<LocationTracking />} />
            <Route path="/earnings" element={<Earnings />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/questions" element={<Questions />} />
            <Route path="/bookings" element={<Bookings />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:id" element={<Products />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
}

export default App;
